import * as React from "react";
import { Box, CPHrBreak, CPText } from "shared/components/base";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPActionsheetModal from "shared/components/shared/CPActionsheetModal/CPActionsheetModal";
import CPTextBox from "shared/components/shared/CPTextBox/CPTextBox";
import CPTextArea from "shared/components/shared/CPTextArea/CPTextArea";
import CPEmailTextBox from "shared/components/shared/CPEmailTextBox/CPEmailTextBox";
import CPListBox, {
  CPBoxOption,
} from "shared/components/shared/CPListBox/CPListBox";
import CPDropDownList from "shared/components/shared/CPDropDownList/CPDropDownList";
import CPModal from "shared/components/shared/CPModal/CPModal";
import CPHelpButton from "shared/components/shared/CPHelpButton/CPHelpButton";
import {
  useAgenciesLookupQuery,
  useRolesLookupQuery,
} from "data/lookup/lookupQueries";
import { Dialog } from "@progress/kendo-react-dialogs";
import useAppState from "shared/state/useAppState";
import {
  PendingUser,
  useUpsertPendingUserMutation,
} from "data/pendingUser/pendingUser";

const SIGNON_OPTIONS = [
  { text: "Microsoft SSO", value: "AZURE_SSO" },
  { text: "Passwordless Signon", value: "LOCAL_IDENTITY" },
];

interface SignonHelpModalProps {
  authProvider: string | undefined;
  open: boolean;
  onClose: () => void;
}

const SignonHelpModal: React.FC<SignonHelpModalProps> = ({
  authProvider,
  open,
  onClose,
}) => (
  <CPModal title="Sign On Options" open={open} onClose={onClose} width={520}>
    <Box flexDirection="column" spacing={24}>
      <Box flexDirection="column" spacing={12}>
        <Box flexDirection="row" alignItems="center" spacing={10}>
          <CPText bold fontSize="15px">
            Microsoft SSO
          </CPText>
          {authProvider === "AZURE_SSO" && (
            <CPText
              style={{
                background: "#e3f2fd",
                color: "#0066cc",
                border: "1px solid #90caf9",
                borderRadius: 3,
                padding: "2px 8px",
                fontSize: 11,
              }}
            >
              Currently selected
            </CPText>
          )}
        </Box>
        <CPText fontSize="13px">
          Best for internal employees and agents who already have a company
          Microsoft account. The user signs in through Microsoft — no separate
          password is created or stored in this system.
        </CPText>
        <Box flexDirection="column" spacing={8} style={{ paddingLeft: 4 }}>
          <CPText fontSize="13px">
            • A user account must exist in Azure Active Directory.
          </CPText>
          <CPText fontSize="13px">
            • The user must be assigned to the appropriate Azure security groups
            for this application.
          </CPText>
          <CPText fontSize="13px">
            • Login and identity are managed entirely by Microsoft.
          </CPText>
        </Box>
      </Box>

      <CPHrBreak />

      <Box flexDirection="column" spacing={12}>
        <Box flexDirection="row" alignItems="center" spacing={10}>
          <CPText bold fontSize="15px">
            Passwordless Sign On
          </CPText>
          {authProvider === "LOCAL_IDENTITY" && (
            <CPText
              style={{
                background: "#e3f2fd",
                color: "#0066cc",
                border: "1px solid #90caf9",
                borderRadius: 3,
                padding: "2px 8px",
                fontSize: 11,
              }}
            >
              Currently selected
            </CPText>
          )}
        </Box>
        <CPText fontSize="13px">
          Best for external users — such as drivers or third-party contacts —
          who do not have a company Microsoft account. Instead of a password,
          the user receives a short-lived verification code by email each time
          they sign in.
        </CPText>
        <Box flexDirection="column" spacing={8} style={{ paddingLeft: 4 }}>
          <CPText fontSize="13px">
            • The user goes to the login page and enters their email address.
          </CPText>
          <CPText fontSize="13px">
            • A one-time code is sent to that email and must be entered to
            complete sign in.
          </CPText>
          <CPText fontSize="13px">
            • The code expires quickly — the user must use it promptly.
          </CPText>
          <CPText fontSize="13px" color="warning">
            • Make sure the email address entered above is correct and actively
            monitored by the user before approving.
          </CPText>
        </Box>
      </Box>
    </Box>
  </CPModal>
);

export class UpdatePendingUserRecord implements Omit<
  PendingUser,
  "updatedBy" | "updatedAt" | "createdAt" | "requestType"
> {
  id: number = 0;
  firstname: string = "";
  lastname: string = "";
  email: string = "";
  roles: string[] = [];
  agencies: string[] = [];
  isApproved: boolean = false;
  reason?: string = "";
  externalSystemId?: string = "";
  authProvider?: string = "";
}

// Deny Confirm Modal
interface DenyConfirmModalProps {
  reason: string;
  onReasonChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export const DenyConfirmModal: React.FC<DenyConfirmModalProps> = ({
  reason,
  onReasonChange,
  onConfirm,
  onCancel,
}) => (
  <Dialog title="Please Confirm" onClose={onCancel} width={400}>
    <Box spacing={10} flexDirection="column" style={{ padding: "12px 10px" }}>
      <CPText align="center" fontSize="16px">
        Are you sure you want to deny this user?
      </CPText>
      <CPTextArea
        value={reason}
        name="reason"
        label="Reason for Denial (optional)"
        rows={3}
        disableResize
        onChange={(e) => onReasonChange(e.value)}
      />
    </Box>
    <CPHrBreak spacing={10} />
    <Box
      spacing={10}
      flexDirection="row"
      justifyContent="space-between"
      style={{ padding: "0 10px 10px" }}
    >
      <CPButton fillMode="outline" onClick={onCancel}>
        Cancel
      </CPButton>
      <CPButton themeColor="primary" onClick={onConfirm}>
        Confirm
      </CPButton>
    </Box>
  </Dialog>
);

// Approve Confirm Modal

export interface ApproveConfirmModalProps {
  firstname: string;
  lastname: string;
  email: string;
  externalSystemId?: string;
  requestType?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ApproveConfirmModal: React.FC<ApproveConfirmModalProps> = ({
  firstname,
  lastname,
  email,
  externalSystemId,
  requestType,
  onConfirm,
  onCancel,
}) => (
  <Dialog title="Verify Before Approving" onClose={onCancel} width={440}>
    <Box spacing={10} flexDirection="column" style={{ padding: "12px 10px" }}>
      <CPText align="center" fontSize="16px">
        Please confirm you have verified the following information is accurate
        before granting access.
      </CPText>
      <CPHrBreak spacing={4} />
      <Box flexDirection="column" spacing={6}>
        <CPText>
          Name: {firstname} {lastname}
        </CPText>
        <CPText>Email: {email}</CPText>
        {externalSystemId && (
          <CPText>Previous Username: {externalSystemId}</CPText>
        )}
      </Box>
      <CPHrBreak spacing={4} />
      <CPText fontSize="13px" color="warning">
        Approving grants this user access to the system. Ensure the email and
        previous username (if provided) match a known, trusted individual.
      </CPText>
    </Box>
    <CPHrBreak spacing={10} />
    <Box
      spacing={10}
      flexDirection="row"
      justifyContent="space-between"
      style={{ padding: "0 10px 10px" }}
    >
      <CPButton fillMode="outline" onClick={onCancel}>
        Cancel
      </CPButton>
      <CPButton themeColor="primary" onClick={onConfirm}>
        Confirm &amp; Approve
      </CPButton>
    </Box>
  </Dialog>
);

// User Details Modal

export interface UserDetailsModalProps {
  selectedUser: PendingUser | null;
  onClose: () => void;
  onSave: () => void;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  selectedUser,
  onClose,
  onSave,
}) => {
  const { setToastMsg, setIsLoading } = useAppState();
  const {
    mutate,
    error,
    isLoading: isUpserting,
  } = useUpsertPendingUserMutation();
  const { data: roles, isLoading: isRolesLoading } = useRolesLookupQuery();
  const { data: agencies, isLoading: isAgenciesLoading } =
    useAgenciesLookupQuery();

  const [showDenyConfirm, setShowDenyConfirm] = React.useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = React.useState(false);
  const [showSignonHelp, setShowSignonHelp] = React.useState(false);
  const [editingUser, setEditingUser] =
    React.useState<UpdatePendingUserRecord | null>(
      selectedUser
        ? {
            id: selectedUser.id,
            firstname: selectedUser.firstname,
            lastname: selectedUser.lastname,
            email: selectedUser.email,
            roles: selectedUser.requestType ? [selectedUser.requestType] : [],
            agencies: Array.isArray(selectedUser.agencies)
              ? selectedUser.agencies
              : (selectedUser.agencies ?? "")
                  .split(",")
                  .map((a) => a.trim())
                  .filter(Boolean),
            isApproved: selectedUser.isApproved ?? false,
            reason: selectedUser.reason ?? "",
            externalSystemId: selectedUser.externalSystemId ?? "",
            authProvider: selectedUser.authProvider ?? "",
          }
        : null,
    );

  const [rolesBoxOptions, setRolesBoxOptions] = React.useState<CPBoxOption>({
    available: [],
    selected: [],
  });
  const [agencyBoxOptions, setAgencyBoxOptions] = React.useState<CPBoxOption>({
    available: [],
    selected: [],
  });

  React.useEffect(() => {
    if (!editingUser || isRolesLoading) return;
    setRolesBoxOptions({
      available: (roles ?? [])
        .filter((r) => !editingUser.roles.includes(r.name))
        .map((r) => ({ label: r.name, value: r.name, selected: false })),
      selected: editingUser.roles.map((r) => ({
        label: r,
        value: r,
        selected: false,
      })),
    });
  }, [isRolesLoading]);

  React.useEffect(() => {
    if (!editingUser || isAgenciesLoading) return;
    setAgencyBoxOptions({
      available: (agencies ?? [])
        .filter((a) => !editingUser.agencies.includes(a.agency))
        .map((a) => ({
          label: `${a.agency} (${a.company})`,
          value: a.agency,
          selected: false,
        })),
      selected: editingUser.agencies.map((r) => ({
        label: r,
        value: r,
        selected: false,
      })),
    });
  }, [isAgenciesLoading]);

  React.useEffect(() => {
    setIsLoading(isUpserting);
  }, [isUpserting]);

  const doApprove = () => {
    if (!editingUser) return;
    mutate(
      {
        ...editingUser,
        agencies: agencyBoxOptions.selected.map((a) => a.value as string),
        roles: rolesBoxOptions.selected.map((r) => r.value as string),
        isApproved: true,
        externalSystemId: editingUser.externalSystemId,
      },
      {
        onSuccess: () => {
          setToastMsg("User approved successfully", "success");
          onSave();
        },
        onError: (error: unknown) => {
          if ((error as Record<string, unknown>)["errors"]) {
            setToastMsg(
              "Please check the form for errors and try again",
              "error",
            );
            return;
          }
          setToastMsg("An error occurred during the request.", "error");
        },
      },
    );
  };

  return (
    <>
      <SignonHelpModal
        authProvider={editingUser?.authProvider}
        open={showSignonHelp}
        onClose={() => setShowSignonHelp(false)}
      />
      {showApproveConfirm && editingUser && (
        <ApproveConfirmModal
          firstname={editingUser.firstname}
          lastname={editingUser.lastname}
          email={editingUser.email}
          externalSystemId={editingUser.externalSystemId}
          requestType={selectedUser?.requestType}
          onConfirm={() => {
            setShowApproveConfirm(false);
            doApprove();
          }}
          onCancel={() => setShowApproveConfirm(false)}
        />
      )}
      {showDenyConfirm && editingUser && (
        <DenyConfirmModal
          reason={editingUser.reason ?? ""}
          onReasonChange={(value) =>
            setEditingUser((prev) => (prev ? { ...prev, reason: value } : prev))
          }
          onConfirm={() => {
            setShowDenyConfirm(false);
            mutate(
              { ...editingUser, isApproved: false },
              {
                onSuccess: () => {
                  setToastMsg("User denied successfully", "success");
                  onSave();
                },
                onError: (error: unknown) => {
                  if ((error as Record<string, unknown>)["errors"]) {
                    setToastMsg(
                      "Please check the form for errors and try again",
                      "error",
                    );
                    return;
                  }

                  setToastMsg("An error occurred during the request.", "error");
                },
              },
            );
          }}
          onCancel={() => setShowDenyConfirm(false)}
        />
      )}
      <CPActionsheetModal
        title="User Details"
        open={selectedUser !== null}
        onClose={onClose}
        position="right"
        width={550}
        actionBtns={
          <>
            <CPButton
              themeColor="primary"
              onClick={() => editingUser && setShowApproveConfirm(true)}
            >
              Approve & Save
            </CPButton>
            <CPButton
              themeColor="warning"
              onClick={() => setShowDenyConfirm(true)}
            >
              Deny Request
            </CPButton>
          </>
        }
      >
        {editingUser && (
          <Box
            spacing={24}
            flexDirection="column"
            style={{ paddingBottom: 16 }}
          >
            <Box spacing={12} flexDirection="column">
              <Box
                flexDirection="row"
                alignItems="flex-start"
                spacing={12}
                className="cp-actionsheet-input-box"
              >
                <CPTextBox
                  name="firstname"
                  label="Firstname"
                  value={editingUser.firstname}
                  required
                  error={error}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      firstname: e.target.value?.toString() || "",
                    })
                  }
                />
                <CPTextBox
                  name="lastname"
                  label="Lastname"
                  value={editingUser.lastname}
                  error={error}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      lastname: e.target.value?.toString() || "",
                    })
                  }
                />
              </Box>

              <Box className="cp-actionsheet-input-box">
                <CPEmailTextBox
                  name="email"
                  label="Email Address"
                  value={editingUser.email}
                  error={error}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      email: e.target.value?.toString() ?? "",
                    })
                  }
                  required
                />
              </Box>

              <Box
                flexDirection="row"
                alignItems="center"
                justifyContent={"flex-start"}
                spacing={6}
                style={{ padding: 0 }}
                className="cp-actionsheet-input-box"
              >
                <Box style={{ flex: 1, maxWidth: "90%" }}>
                  <CPDropDownList
                    name="authProvider"
                    label="Sign on option"
                    required
                    data={SIGNON_OPTIONS}
                    textField="text"
                    dataItemKey="value"
                    valueKey="value"
                    value={editingUser.authProvider ?? null}
                    onChange={(e) =>
                      setEditingUser({
                        ...editingUser,
                        authProvider: e.value as string,
                      })
                    }
                  />
                </Box>
                <CPHelpButton
                  onClick={() => setShowSignonHelp(true)}
                  title="Learn about sign on options"
                  aria-label="Sign on option help"
                />
              </Box>

              <Box className="cp-actionsheet-input-box">
                <CPTextBox
                  name="externalSystemId"
                  label="External System Username"
                  value={editingUser.externalSystemId ?? ""}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      externalSystemId: e.value as string,
                    })
                  }
                  helpText="The main username used in other Evans systems (GreatEdge, EBH, etc)"
                />
              </Box>
            </Box>

            <Box spacing={12} flexDirection="column">
              <Box className="cp-actionsheet-input-box">
                <CPListBox
                  headerText="Assigned Agencies"
                  boxOptions={agencyBoxOptions}
                  onChange={setAgencyBoxOptions}
                  filterable={true}
                />
              </Box>

              <Box className="cp-actionsheet-input-box">
                <CPListBox
                  headerText="Assigned Roles"
                  boxOptions={rolesBoxOptions}
                  onChange={setRolesBoxOptions}
                  filterable={true}
                />
              </Box>
            </Box>
          </Box>
        )}
      </CPActionsheetModal>
    </>
  );
};

export default UserDetailsModal;
